import React, { useState, useEffect } from 'react';
import { Mail, Upload, Building2, ArrowRight, Lightbulb } from 'lucide-react';
import EmailIngest from '../components/EmailIngest';
import ProviderRequest from '../components/ProviderRequest';
import { apiFetch } from '../utils/api';

/** Purpose: Executes { on upload click, requested section }. Inputs: { onUploadClick, requestedSection }. Output: Operation result. */
export default function Import({ onUploadClick, requestedSection }) {
  const [activeSection, setActiveSection] = useState(null);
  const [authorizations, setAuthorizations] = useState([]);

  useEffect(() => {
    loadAuthorizations();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) {
      setActiveSection(section);
    }
  }, []);

  useEffect(() => {
    if (requestedSection?.type === 'request-provider' && requestedSection?.token) {
      setActiveSection('request');
    }
    if (requestedSection?.type === null && requestedSection?.token) {
      setActiveSection(null);
    }
  }, [requestedSection]);

  /** Purpose: Loads authorizations. Inputs: none. Output: Promise with operation result. */
  const loadAuthorizations = async () => {
    try {
      const response = await apiFetch('/permissions');
      if (response.ok) {
        const data = await response.json();
        setAuthorizations(data.data || []);
      }
    } catch (err) {
      console.error('Failed to load authorizations:', err);
    }
  };

  const importMethods = [
    {
      id: 'email',
      icon: Mail,
      title: 'Import by Email',
      description: 'Have providers send records directly to your personal email address',
      color: 'blue',
      available: true
    },
    {
      id: 'upload',
      icon: Upload,
      title: 'Import Files',
      description: 'Upload PDFs, images, and documents from your device',
      color: 'green',
      available: true,
      action: onUploadClick
    },
    {
      id: 'request',
      icon: Building2,
      title: 'Request From Provider',
      description: 'Send a HIPAA authorization request to a healthcare provider',
      color: 'purple',
      available: true
    },
  ];

  const COLOR_STYLES = {
    blue: {
      card: 'border-slate-200 hover:border-blue-500 hover:bg-blue-50',
      icon: 'bg-blue-100 text-blue-600',
      action: 'text-blue-600'
    },
    green: {
      card: 'border-slate-200 hover:border-green-500 hover:bg-green-50',
      icon: 'bg-green-100 text-green-600',
      action: 'text-green-600'
    },
    purple: {
      card: 'border-slate-200 hover:border-purple-500 hover:bg-purple-50',
      icon: 'bg-purple-100 text-purple-600',
      action: 'text-purple-600'
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Import Records</h2>
        <p className="text-slate-600 mt-1">Use import and request workflows to bring records into MedCaddy.</p>
      </div>

      {/* Import Methods Grid */}
      {!activeSection && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {importMethods.map((method) => {
            const Icon = method.icon;
            const styles = COLOR_STYLES[method.color] || COLOR_STYLES.blue;
            
            return (
              <button
                key={method.id}
                onClick={() => {
                  if (method.action) {
                    method.action();
                  } else if (method.available) {
                    setActiveSection(method.id);
                  }
                }}
                disabled={!method.available}
                className={`relative p-5 sm:p-6 rounded-xl border-2 text-left transition-all ${
                  method.available 
                    ? styles.card
                    : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${styles.icon}`}>
                  <Icon className="w-6 h-6" />
                </div>
                
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1">{method.title}</h3>
                <p className="text-sm text-slate-600 mb-4">{method.description}</p>
                
                {method.available && (
                  <span className={`inline-flex items-center gap-1 text-sm font-medium ${styles.action}`}>
                    {method.action ? 'Open' : 'Set up'} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Active Section */}
      {activeSection === 'email' && (
        <div>
          <button
            onClick={() => setActiveSection(null)}
            className="mb-4 text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          >
            ← Back to all import methods
          </button>
          <EmailIngest />
        </div>
      )}

      {activeSection === 'request' && (
        <div>
          <button
            onClick={() => setActiveSection(null)}
            className="mb-4 text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          >
            ← Back to all import methods
          </button>
          <ProviderRequest authorizations={authorizations} />
        </div>
      )}

      {/* Quick Tips */}
      {!activeSection && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Quick Tips
          </h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span><strong>Import by email</strong> - Give your personal address to providers and they can send records directly</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span><strong>Request from provider</strong> - Create a formal HIPAA request to send to any healthcare provider</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span><strong>All files are encrypted</strong> - Your records are encrypted at rest for HIPAA compliance</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
